import { ENV } from '@/config/env';

export interface CompanyInvitation {
  id: string;
  companyId: string;
  companyName: string;
  inviterId: string;
  inviterName: string;
  inviteeEmail: string;
  inviteeId?: string;
  invitationCode: string;
  companyRole: 'manager' | 'technician' | 'apprentice' | 'admin';
  employmentType: 'employee' | 'contractor' | 'freelancer' | 'partner';
  proposedHourlyRate?: number;
  proposedCommissionRate?: number;
  canTakePersonalJobs: boolean;
  permissions: string[];
  message?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled';
  expiresAt: Date;
  acceptedAt?: Date;
  rejectedAt?: Date;
  rejectionReason?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CompanyMember {
  id: string;
  userId: string;
  companyId: string;
  role: 'admin' | 'manager' | 'technician' | 'apprentice';
  employmentType: 'employee' | 'contractor' | 'freelancer' | 'partner';
  hourlyRate: number;
  commissionRate: number;
  permissions: string[];
  isActive: boolean;
  joinedAt: Date;
  // User details
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  avatar?: string;
}

export interface CompanyStats {
  totalMembers: number;
  activeMembers: number;
  totalJobsCompleted: number;
  totalRevenue: number;
  averageTeamRating: number;
  monthlyRevenue: number;
  monthlyJobs: number;
}

class CompanyService {
  private baseUrl = ENV.API_BASE_URL;

  // Check if user is a company owner
  isCompanyOwner(user: any): boolean {
    return user?.role === 'provider' && (user?.providerType === 'COMPANY' || user?.providerType === 'company');
  }

  // Get company profile
  async getCompanyProfile(userId: string) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/profile`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': userId,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch company profile');
    }

    return response.json();
  }

  // Get company stats
  async getCompanyStats(userId: string): Promise<CompanyStats> {
    console.log('📡 CompanyService.getCompanyStats:', { userId, baseUrl: this.baseUrl });
    
    const url = `${this.baseUrl}/api/v1/auth/company/stats`;
    const token = localStorage.getItem('auth_token');
    
    console.log('📡 Making request to:', url);
    console.log('📡 Headers:', { hasToken: !!token, userId });
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-User-ID': userId,
        'Content-Type': 'application/json'
      }
    });

    console.log('📥 Response:', { status: response.status, statusText: response.statusText, ok: response.ok });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch company stats:', { status: response.status, error: errorText });
      throw new Error(`Failed to fetch company stats: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    console.log('✅ Company stats fetched:', data);
    return data.data || data;
  }

  // Get company members
  async getCompanyMembers(userId: string): Promise<CompanyMember[]> {
    console.log('📡 CompanyService.getCompanyMembers:', { userId, baseUrl: this.baseUrl });
    
    const url = new URL(`${this.baseUrl}/api/v1/auth/company/members`);
    if (userId) {
      url.searchParams.set('companyId', userId);
    }
    const token = localStorage.getItem('auth_token');
    
    console.log('📡 Making request to:', url.toString());
    
    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-User-ID': userId,
        'Content-Type': 'application/json'
      }
    });

    console.log('📥 Response:', { status: response.status, statusText: response.statusText, ok: response.ok });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch company members:', { status: response.status, error: errorText });
      throw new Error(`Failed to fetch company members: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    const members = data.data || data;
    console.log('✅ Company members fetched:', { count: members?.length || 0 });

    return (members || []).map((member: any) => {
      const nameParts = (member.name || '').trim().split(/\s+/).filter(Boolean);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ');
      const email = member.email || '';
      const fallbackFirstName = firstName || email.split('@')[0] || 'Member';

      return {
        id: member.id,
        userId: member.userId || member.user_id || '',
        companyId: member.providerId || member.provider_id || '',
        role: member.role,
        employmentType: member.employmentType || member.employment_type || 'employee',
        hourlyRate: member.hourlyRate || member.hourly_rate || 0,
        commissionRate: member.commissionRate || member.commission_rate || 0,
        permissions: member.permissions || [],
        isActive: member.isActive ?? member.status === 'active',
        joinedAt: member.joinedAt || member.joined_date || member.joinedDate,
        firstName: member.firstName || member.first_name || fallbackFirstName,
        lastName: member.lastName || member.last_name || lastName,
        email,
        phone: member.phone || '',
        avatar: member.avatar || member.profileImage
      } as CompanyMember;
    });
  }

  // Get company invitations
  async getCompanyInvitations(userId: string): Promise<CompanyInvitation[]> {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/invitations`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': userId,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch invitations');
    }

    const data = await response.json();
    return data.data || data;
  }

  // Send invitation
  async sendInvitation(invitationData: Omit<CompanyInvitation, 'id' | 'companyId' | 'status' | 'createdAt' | 'expiresAt'>) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/invitations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': localStorage.getItem('user_id') || '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(invitationData)
    });

    if (!response.ok) {
      throw new Error('Failed to send invitation');
    }

    return response.json();
  }

  // Update member
  async updateMember(memberId: string, updates: Partial<CompanyMember>) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/members/${memberId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': localStorage.getItem('user_id') || '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error('Failed to update member');
    }

    return response.json();
  }

  // Remove member
  async removeMember(memberId: string) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/members/${memberId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': localStorage.getItem('user_id') || '',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to remove member');
    }

    return response.json();
  }

  // Get member performance
  async getMemberPerformance(memberId: string, period: 'week' | 'month' | 'year' = 'month') {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/company/members/${memberId}/performance?period=${period}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': localStorage.getItem('user_id') || '',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch member performance');
    }

    return response.json();
  }

  // Get received invitations (for individual providers)
  async getReceivedInvitations(email: string): Promise<CompanyInvitation[]> {
    const normalizedEmail = encodeURIComponent(email.trim());
    const response = await fetch(`${this.baseUrl}/api/v1/auth/invitations/received?email=${normalizedEmail}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': localStorage.getItem('user_id') || '',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch received invitations');
    }

    const data = await response.json();
    return data.data || data;
  }

  // Accept invitation
  async acceptInvitation(invitationId: string) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/invitations/${invitationId}/accept`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': localStorage.getItem('user_id') || '',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to accept invitation');
    }

    return response.json();
  }

  // Reject invitation
  async rejectInvitation(invitationId: string, reason?: string) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/invitations/${invitationId}/reject`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': localStorage.getItem('user_id') || '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ reason })
    });

    if (!response.ok) {
      throw new Error('Failed to reject invitation');
    }

    return response.json();
  }

  // Update user company association (after accepting invitation)
  async updateUserCompanyAssociation(userId: string, data: {
    parentCompanyId: string;
    companyRole: string;
    employmentType: string;
    canTakePersonalJobs: boolean;
    companyPermissions: string[];
    hourlyRate: number;
  }) {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/users/${userId}/company-association`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'X-User-ID': userId,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error('Failed to update company association');
    }

    return response.json();
  }
}

export const companyService = new CompanyService();
